import { ref } from "vue";
import { useToast } from "primevue/usetoast";
import {
  addUserToTeam,
  deleteUserFromTeam,
  getTeams,
} from "@/modules/teams/api";
import type { ProfileTeams } from "@/api/model";

export function useUserTeams(userId: number, organizationId?: number) {
  const toast = useToast();
  const isLoading = ref(false);
  const teams = ref<ProfileTeams[]>([]);
  const allAvailableTeams = ref<{ id: number; name: string }[]>([]);

  async function loadUserTeams(userTeams: ProfileTeams[]) {
    teams.value = userTeams || [];
  }

  async function loadAvailableTeams() {
    if (!organizationId) {
      return;
    }

    isLoading.value = true;
    try {
      const response = await getTeams(1000, 0, organizationId);

      if (!("error" in response)) {
        const teamList = response.results || [];
        allAvailableTeams.value = teamList.map((t) => ({
          id: t.id,
          name: t.name || "",
        }));
      }
    } catch (error) {
      // Error loading teams
    } finally {
      isLoading.value = false;
    }
  }

  async function addTeam(teamId: number): Promise<boolean> {
    isLoading.value = true;
    try {
      const result = await addUserToTeam(teamId, userId);

      // Check for error (result could be undefined/null for success responses)
      if (result && typeof result === "object" && "error" in result) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: result.error,
          life: 5000,
        });
        return false;
      }

      // If we get here, the add was successful
      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Added to team",
        life: 3000,
      });
      return true;
    } catch (error) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to add to team",
        life: 5000,
      });
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function removeTeam(teamId: number): Promise<boolean> {
    isLoading.value = true;
    try {
      const result = await deleteUserFromTeam(userId, teamId);

      // Check for error (result could be undefined/null for 204 responses)
      if (result && typeof result === "object" && "error" in result) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: result.error,
          life: 5000,
        });
        return false;
      }

      // If we get here, the delete was successful (204 or other success)
      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Removed from team",
        life: 3000,
      });
      return true;
    } catch (error) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to remove from team",
        life: 5000,
      });
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    teams,
    allAvailableTeams,
    isLoading,
    loadUserTeams,
    loadAvailableTeams,
    addTeam,
    removeTeam,
  };
}
